import { ReparoApi } from '@api/index';
import {
    getDrrTrend,
    getInventoryHealth,
    type InventoryHealthRow,
    type RatePolicyBasis,
} from '@api/replenishment-planning';
import DataSheet, {
    type DataSheetCaptureHandle,
    type DataSheetColumn,
} from '@components/ui/DataSheet';
import GroupingLevels from '@components/ui/GroupingLevels';
import { RateBasisControl } from '@components/ui/RateBasisControl';
import useToaster from '@hooks/toaster/useToaster';
import {
    ActionIcon,
    Anchor,
    Button,
    Checkbox,
    Group,
    SegmentedControl,
    Select,
    Stack,
    Text,
    Tooltip,
} from '@mantine/core';
import { FactoryIcon, ShoppingCartIcon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type FC,
    type ReactNode,
} from 'react';

import type { SortBy } from '../../../api/inventory';
import { getOrgConfig } from '../../../api/organization';
import {
    type GroupBy,
    MATERIAL_GROUPINGS,
    NO_GROUPING,
    PRODUCT_GROUPINGS,
    resolveOrgGrouping,
} from '../../../constants/replenishmentGrouping';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import usePersistentState from '../../../hooks/usePersistentState';
import { useSkuExplosion } from '../../../hooks/useSkuExplosion';
import { compareBySortBy } from '../../../utils/inventorySort';
import { explosionSourceIds } from '../../../utils/skuExplosion';
import { ColumnPicker } from '../../ui/ColumnPicker';
import NewWorkOrderModal from '../workOrders/NewWorkOrderModal';

import CreatePoFromShortfallModal from './CreatePoFromShortfallModal';
import DrrDelta from './DrrDelta';
import ExplosionDetailPanel from './ExplosionDetailPanel';
import {
    addScaledHealthRow,
    cloneHealthRow,
    getHealthRowId,
    makeEmptyHealthRow,
    recomputeHealthRow,
} from './explosion';
import { buildGroups, type HealthRow, toRow } from './grouping';
import InboundDetailPanel, { type InboundFocus } from './InboundDetailPanel';
import AiSummaryPanel from '../../ui/AiSummaryPanel';
import { INVENTORY_HEALTH_SUMMARY_SPEC } from '../../../constants/summary-specs';

import ReorderPointUploadModal from './ReorderPointUploadModal';
import ReplenishmentSettingsModal from './ReorderRulesModal';
import RopWindowCell from './RopWindowCell';
import { computeContributions, computeTotals, formatShare } from './totals';
import StockTransferModal from '../StockTransferModal';
import { STOCK_TRANSFER_LABEL } from '../stockTransfer.constants';
import StockTransferHeader from '../StockTransferHeader';
import { formatCount } from '@utils/currency';

type ItemView = 'product' | 'material';

/** Same wording as the Inventory grids' Sort By, so the two pages read alike. */
const SORT_OPTIONS: { value: SortBy; label: string }[] = [
    { value: 'DEFAULT', label: 'Default Order' },
    { value: 'TOP_SELLING', label: 'Top Selling' },
    { value: 'LOW_STOCK', label: 'Low on Stock' },
    { value: 'OVER_STOCK', label: 'Overstock' },
];

const SOURCING_FILTERS: { value: string; label: string }[] = [
    { value: 'ALL', label: 'All' },
    { value: 'BUY', label: 'FG PO vendors' },
    { value: 'MAKE', label: 'Production vendors' },
];


/**
 * Columns the picker never offers. `productId` is the row's identity — hiding it leaves
 * a grid of numbers with nothing to attach them to — and `sourcingModel` is the create-PO
 * / create-WO affordance rather than a readable value.
 */
const NON_HIDEABLE_COLUMNS = new Set(['productId', 'sourcingModel']);

/**
 * The reorder-window badge is read-only here, so its change handler goes nowhere. Hoisted to
 * a module constant rather than an inline arrow: the cell is memoised, and a fresh function
 * per render would defeat that on every row of the grid.
 */
const NOOP_WINDOW_CHANGE = () => {};


/**
 * Inventory health — the landing view. Today's actual position for every item against its
 * reorder point, with no plan run and nothing persisted. Deficit here is simply
 * `reorderPoint − (stock + inbound)`; the user decides whether and when to order.
 *
 * The plan (the other mode) is the forward-looking answer — it walks periods, nets forecast
 * consumption and back-dates by lead time to tell you WHEN. Both read the same reorder
 * point, so they agree on the target even though they net different things against it.
 */
type Props = {
    /**
     * When given, finished-goods rows grow a select checkbox and the "Plan for these SKUs"
     * bar — the handoff into Plan mode, scoped to the picked products.
     */
    onPlanForSkus?: (productIds: string[]) => void;
};

const InventoryHealthView: FC<Props> = ({ onPlanForSkus }) => {
    const { showToastSuccess, showToastWarning } = useToaster();
    const { currentUser } = useCurrentUser();
    const merchantId = currentUser?.organizationId;

    const [rows, setRows] = useState<InventoryHealthRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [summaryCollapsed, setSummaryCollapsed] = useState(false);
    const [targetDoh, setTargetDoh] = useState<number | null>(null);

    // Column visibility, per merchant and per browser — the same shape the Inventory
    // grids use. Empty means everything shows, so the table opens as it always has.
    const [hiddenCols, setHiddenCols] = usePersistentState<string[]>(
        `replenishment_health_hidden_cols_${merchantId ?? 'default'}`,
        [],
    );

    const [itemView, setItemView] = useState<ItemView>('product');

    /**
     * Grouping is the brand's default until someone changes it on this visit, then their
     * choice for the rest of it. Nothing is persisted per user, so a reload returns to the
     * brand default — the same resolve-don't-store shape the DoH view uses for its own
     * default, minus the localStorage half.
     *
     * The second level is only meaningful once a first is chosen; the pair is held together
     * so it can never be half-updated into a state GroupingLevels would not produce.
     */
    const [groupingChoice, setGroupingChoice] = useState<{
        groupBy: GroupBy;
        subGroupBy: GroupBy;
    } | null>(null);

    // Already fetched by SideNavigationBar on every page, so this is a warm-cache read.
    const { data: orgConfig } = useQuery({
        queryKey: ['org-config', merchantId],
        queryFn: () => getOrgConfig(merchantId!),
        enabled: !!merchantId,
        staleTime: 5 * 60 * 1000,
    });

    const orgGrouping = useMemo(
        () =>
            resolveOrgGrouping(
                orgConfig?.inventoryViewDefaults?.replenishmentHealth?.groupBy,
                orgConfig?.inventoryViewDefaults?.replenishmentHealth?.subGroupBy,
            ),
        [orgConfig],
    );

    const { groupBy, subGroupBy } = groupingChoice ?? orgGrouping;
    // The per-tier reorder rule lives here, on the surface that acts on it — target cover
    // is the plan's input and is set on the plan view.
    /**
     * Row ordering. DEFAULT is worst-shortfall-first, which is what this page has always
     * opened on and the reason it exists — the rest are for reading the same rows a
     * different way. Applies to group rows as well as leaves, so Top Selling surfaces the
     * group whose members sell most rather than the one with the biggest gap.
     */
    const [sortBy, setSortBy] = useState<SortBy>('DEFAULT');
    const [reorderRulesOpen, setReorderRulesOpen] = useState(false);
    const [sourcing, setSourcing] = useState<string>('ALL');
    const [search, setSearch] = useState('');

    // Rate basis: saved org default + an unsaved per-request preview, matching the
    // Inventory tab's explore-then-commit control.
    const [savedBasis, setSavedBasis] = useState<RatePolicyBasis>('BACKWARD');
    const [savedWindow, setSavedWindow] = useState(30);
    const [draftBasis, setDraftBasis] = useState<RatePolicyBasis | null>(null);
    const [draftWindow, setDraftWindow] = useState<number | null>(null);
    const [savingRate, setSavingRate] = useState(false);

    // Which row's inbound is open, and WHICH HALF of it was clicked — the window answers
    // about the number the operator pressed, not about inbound in general.
    const [inboundFor, setInboundFor] = useState<{
        row: HealthRow;
        focus: InboundFocus;
    } | null>(null);
    /** Which exploded row's derivation is open. Null when the window is closed. */
    const [explosionFor, setExplosionFor] = useState<HealthRow | null>(null);
    const captureRef = useRef<DataSheetCaptureHandle | null>(null);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [poDraft, setPoDraft] = useState<HealthRow | null>(null);
    const [woDraft, setWoDraft] = useState<HealthRow | null>(null);

    const effectiveBasis = draftBasis ?? savedBasis;
    const effectiveWindow = draftWindow ?? savedWindow;
    // The window counts on BOTH bases: it is a LENGTH, and the basis only says which
    // direction it points in — 30 days of sales behind today, or 30 days of forecast ahead.
    const rateDirty =
        (draftBasis !== null && draftBasis !== savedBasis) ||
        (draftWindow !== null && draftWindow !== savedWindow);


    const load = useCallback(() => {
        setLoading(true);
        getInventoryHealth({
            itemType: itemView,
            basis: effectiveBasis,
            drrWindow: effectiveWindow,
            // `sourcingModel` is deliberately NOT sent. The server applies it as its very
            // last step — a plain `rows.filter(r => r.sourcingModel === x)` over a result it
            // has already computed in full — so passing it bought nothing but cost a whole
            // round trip of stock, inbound and rate queries on every toggle of a control
            // that is meant to feel instant. Filtered client-side below instead.
        })
            .then((res) => {
                // Normalise once, here. These fields are newer than the rest of the payload,
                // so a backend that hasn't been restarted answers without them — and an
                // absent array is not something a render path should have to defend against
                // row by row.
                setRows(
                    (res.data ?? []).map((row) => ({
                        ...row,
                        inbound: {
                            ...row.inbound,
                            // Derived server-side, but re-derivable from the four legs — so a
                            // backend that predates the split still renders both columns
                            // rather than two blanks.
                            pendingPo: row.inbound.pendingPo ?? 0,
                            pendingWo: row.inbound.pendingWo ?? 0,
                            inTransit:
                                row.inbound.inTransit ??
                                row.inbound.inTransitPo + row.inbound.inTransitStn,
                            open:
                                row.inbound.open ??
                                row.inbound.inProductionPo +
                                    row.inbound.inProductionWo +
                                    (row.inbound.pendingPo ?? 0) +
                                    (row.inbound.pendingWo ?? 0),
                        },
                        stockInternal: row.stockInternal ?? row.currentStock ?? 0,
                        stockAtManufacturer: row.stockAtManufacturer ?? 0,
                        inTransitInternal:
                            row.inTransitInternal ?? row.inbound?.inTransitPo ?? 0,
                        inTransitAtManufacturer: row.inTransitAtManufacturer ?? 0,
                        fgDeficit: row.fgDeficit ?? 0,
                        rmRequired: row.rmRequired ?? 0,
                    })),
                );
                setTargetDoh(res.targetDaysOfCover);
                // Echo back the EFFECTIVE policy the server applied, so the control shows
                // the org default rather than this component's optimistic guess.
                if (itemView === 'product' && draftBasis === null && draftWindow === null) {
                    setSavedBasis(res.basis);
                    setSavedWindow(res.drrWindowDays);
                }
            })
            .catch(() => setRows([]))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemView, effectiveBasis, effectiveWindow]);

    useEffect(load, [load]);

    /**
     * The DRR trend, fetched AFTER the grid has painted and merged into the rows in place.
     *
     * Deliberately a second request rather than extra fields on the health response. Computing
     * it server-side inline cost ~2.2s on basil — the previous window needs its own pass through
     * the stockout gate — which would be 2.2s of nothing on screen. The DRR column is complete
     * and correct the moment the first response lands; the arrow beside it is an annotation, and
     * annotations do not get to hold up a landing view.
     *
     * Skipped only on the materials view, which has no run rates at all. It runs on BOTH bases:
     * under forecast the server compares the forecast rate against what the brand ACTUALLY sold
     * over the same window length, which is the comparison a planner reading a forecast is
     * making anyway — not next week's forecast against last week's, which nothing stores.
     */
    useEffect(() => {
        if (itemView === 'material') return;
        let cancelled = false;
        getDrrTrend({
            basis: effectiveBasis,
            drrWindow: effectiveWindow,
        })
            .then((res) => {
                // A response for a window the operator has since moved off would annotate the
                // wrong rate — silently, because the arrow looks the same either way.
                if (cancelled || res.drrWindowDays !== effectiveWindow) return;
                const byProduct = new Map(
                    res.trends.map((trend) => [trend.productId, trend]),
                );
                setRows((prev) =>
                    prev.map((row) => {
                        const trend = byProduct.get(row.productId);
                        return trend
                            ? {
                                  ...row,
                                  drrPrevious: trend.drrPrevious,
                                  drrDeltaPct: trend.drrDeltaPct,
                              }
                            : row;
                    }),
                );
            })
            // A missing trend leaves the column blank. It is a cue, not a number the page
            // depends on, so a failure here must not surface as an error on a working grid.
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
        // `rows` is deliberately absent: this merges INTO rows, so depending on them would
        // re-fire on its own result forever.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemView, effectiveBasis, effectiveWindow, loading]);

    // Switching FG ↔ RM invalidates a grouping that only exists on the other side.
    useEffect(() => {
        const stillValid = (current: GroupBy): GroupBy => {
            if (itemView === 'material' && (current === 'PRODUCT_GROUP' || current === 'TIER')) {
                return NO_GROUPING;
            }
            if (itemView === 'product' && current === 'MATERIAL_TYPE') return NO_GROUPING;
            return current;
        };
        setGroupingChoice((prev) => {
            const current = prev ?? orgGrouping;
            const nextGroupBy = stillValid(current.groupBy);
            // No first level means no second, matching GroupingLevels' own rule.
            const nextSubGroupBy =
                nextGroupBy === NO_GROUPING
                    ? NO_GROUPING
                    : stillValid(current.subGroupBy);
            // Nothing to correct — stay null so the brand default keeps applying rather
            // than being frozen into a user choice the user never made.
            if (
                nextGroupBy === current.groupBy &&
                nextSubGroupBy === current.subGroupBy
            ) {
                return prev;
            }
            return { groupBy: nextGroupBy, subGroupBy: nextSubGroupBy };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemView, orgGrouping]);

    const handleSaveRateDefault = async () => {
        if (!merchantId) return;
        setSavingRate(true);
        try {
            await ReparoApi({
                method: 'PATCH',
                url: `/organizations/${merchantId}`,
                body: JSON.stringify({
                    lookup: effectiveBasis,
                    salesLookbackDays: effectiveWindow,
                }),
            });
            setSavedBasis(effectiveBasis);
            setSavedWindow(effectiveWindow);
            setDraftBasis(null);
            setDraftWindow(null);
            showToastSuccess({
                title: 'Saved as default',
                subtitle: 'Alerts, reports and planning will now use this basis.',
            });
        } catch {
            showToastWarning({ title: 'Could not save the default. Please try again.' });
        } finally {
            setSavingRate(false);
        }
    };

    const isMaterial = itemView === 'material';

    const groupingOptions = isMaterial ? MATERIAL_GROUPINGS : PRODUCT_GROUPINGS;
    const labelOf = (value: GroupBy): string =>
        groupingOptions.find((option) => option.value === value)?.label ?? 'No grouping';

    /**
     * Rows restated one level down the product relation graph, when the SKU column's
     * break-down toggle is on.
     *
     * Runs on the WHOLE fetched set, deliberately upstream of the search and the sourcing
     * filter. A filter decides which rows you see; it must never decide what a row is made of.
     *
     * Getting this backwards is not a subtle error. `BSL-SSD-BNT-00010` breaks down from three
     * places — itself (2 units), `BSL-SS-FCS-BNT-00010` (6) and `BSL-SSD-BNT-00010-CUST` (0).
     * Searching the base id does not match the clearance parent, because the base is `SSD` and
     * the variant is `SS-FCS`. Filter first and those 6 units are gone before the explosion can
     * fold them in, so the row reads 2 unsearched-8 / searched-2: the same SKU showing two
     * different on-hand figures depending on whether you typed its name.
     *
     * Materials have no combo graph of their own, so the control is not offered there.
     */
    const { rows: explodedRows, explosion } = useSkuExplosion<InventoryHealthRow>({
        rows,
        disabled: isMaterial,
        getId: getHealthRowId,
        cloneRow: cloneHealthRow,
        addScaled: addScaledHealthRow,
        recompute: recomputeHealthRow,
        makeEmptyRow: makeEmptyHealthRow,
    });

    /**
     * The rows exactly as the server sent them, by product id. The panel that explains a
     * break-down needs each contributor's OWN figures, and a contributor no longer exists as
     * a row in the grid — that is precisely what the explosion did to it.
     */
    const sourceRowsById = useMemo(
        () => new Map(rows.map((row) => [row.productId, row])),
        [rows],
    );

    const filteredRows = useMemo(() => {
        const term = search.trim().toLowerCase();
        // Same predicate the server applied when this was a request parameter, so the two
        // cannot disagree about what "FG PO vendors" means.
        const scoped =
            sourcing === 'ALL'
                ? explodedRows
                : explodedRows.filter((r) => r.sourcingModel === sourcing);
        return term
            ? scoped.filter(
                  (r) =>
                      r.productId.toLowerCase().includes(term) ||
                      (r.sku ?? '').toLowerCase().includes(term) ||
                      (r.productName ?? '').toLowerCase().includes(term),
              )
            : scoped;
    }, [explodedRows, search, sourcing]);

    /** One row per item, worst shortfall first — what the grid shows ungrouped. */
    /**
     * The chosen ordering, or undefined under DEFAULT so each level keeps its own
     * worst-shortfall-first sort. Leaves and group rows expose the same two figures — a
     * group's DRR is its members' summed, its cover recomputed from that sum — so one
     * comparator serves both.
     */
    const compareRows = useMemo(
        () =>
            compareBySortBy<{ drr: number; coverDays: number | null }>(sortBy, {
                rateOf: (row) => row.drr,
                coverOf: (row) => row.coverDays,
            }),
        [sortBy],
    );

    const flatRows = useMemo(
        () =>
            [...filteredRows]
                .sort(
                    compareRows ??
                        ((a, b) =>
                            b.shortfall - a.shortfall ||
                            b.currentStock - a.currentStock),
                )
                .map(toRow),
        [filteredRows, compareRows],
    );

    const visibleRows = useMemo(
        () =>
            groupBy === 'NONE'
                ? flatRows
                // Grouping happens LAST, over exploded-then-filtered leaves, so a group's
                // totals are summed from the rows the grid is actually showing.
                : buildGroups(filteredRows, groupBy, subGroupBy, compareRows),
        [flatRows, filteredRows, groupBy, subGroupBy, compareRows],
    );

    /**
     * The rows the grid is actually showing, reported back by DataSheet after its per-column
     * filters have run.
     *
     * Totals and shares are computed from THESE, not from `visibleRows`, so a column filter
     * re-bases both — filter to one category and the percentages add to 100% of that
     * category rather than to some fraction of a page total that is no longer on screen.
     * Pagination is deliberately not in the picture: DataSheet hands over the whole filtered
     * set and slices for display afterwards, so a page-size change never moves these numbers.
     *
     * Seeded with `visibleRows` so the first paint (before DataSheet's effect fires) shows
     * totals rather than zeroes.
     */
    const [gridRows, setGridRows] = useState<HealthRow[]>([]);
    const displayedRows = gridRows.length > 0 ? gridRows : visibleRows;

    const totals = useMemo(() => computeTotals(displayedRows), [displayedRows]);
    const contributions = useMemo(
        () => computeContributions(displayedRows, isMaterial),
        [displayedRows, isMaterial],
    );

    /** The placement a row offers, or null when nothing can be placed against it. */
    const placementFor = useCallback(
        (row: HealthRow): { label: string; onClick: () => void } | null => {
            if (row.sourcingModel === 'UNMAPPED') return null;
            // Materials and BUY products are bought; MAKE products are produced.
            if (isMaterial || row.sourcingModel === 'BUY') {
                // A purchase order goes to exactly one vendor. A group whose children do not
                // agree on one has nobody to raise it with, so it offers no action rather
                // than opening a modal addressed to "Unassigned supplier".
                if (!row.vendorId) return null;
                return {
                    label: row.isGroup
                        ? `Create PO — ${row.vendorName}`
                        : 'Create PO',
                    onClick: () => setPoDraft(row),
                };
            }
            // MAKE rows go to a manufacturer, and a manufacturer group is placeable for the
            // same reason a supplier group is: one order, one factory, many SKUs. A group
            // whose children disagree on the manufacturer has nobody to hand it to.
            if (row.isGroup && !row.vendorId) return null;
            return {
                label: row.isGroup
                    ? `Create production order — ${row.vendorName}`
                    : 'Create production order',
                onClick: () => setWoDraft(row),
            };
        },
        [isMaterial],
    );

    /** The SKU whose open stock transfers are showing in the drawer. */
    const [stockTransferDrill, setStockTransferDrill] = useState<{
        productId: string;
        skuLabel: string;
    } | null>(null);
    const onStockTransferClick = useCallback(
        (row: HealthRow) =>
            setStockTransferDrill({
                productId: row.productId,
                skuLabel: `${row.sku ?? row.productId} – ${row.productName ?? ''}`,
            }),
        [],
    );

    // Products only, and only for brands with transfers between their own warehouses.
    const hasStockTransfer = useMemo(
        () => rows.some((row) => row.stockTransfer != null),
        [rows],
    );

    const allColumns = useMemo<DataSheetColumn<HealthRow>[]>(() => {
        const leafLabel = isMaterial ? 'material' : 'SKU';
        /**
         * Share of the parent total — of the page when the row is top-level, of the group
         * when it was revealed by expanding one. Measured on DRR for finished goods and on
         * RM needed for materials (a material has no run rate of its own); the tooltip says
         * which, because a bare "%" column invites the reader to assume it means whichever
         * number their eye is on.
         */
        const contributionColumn: DataSheetColumn<HealthRow> = {
            key: 'contribution',
            header: '% contribution',
            align: 'right',
            width: 130,
            sortable: true,
            // The figure is derived per row rather than stored on it, so sort, the column
            // filters and the CSV all have to be pointed at the same map the cell reads —
            // the default row[key] lookup would compare undefined and export blanks.
            valueAccessor: (row) => contributions.get(row.rowKey) ?? null,
            csvValue: (_value, row) => formatShare(contributions.get(row.rowKey)),
            render: (_value, row) => {
                const share = contributions.get(row.rowKey) ?? null;
                return (
                    <Tooltip
                        label={`Share of ${isMaterial ? 'RM needed' : 'DRR'} — against the whole grid for a top-level row, against its own group for a row inside one.`}
                    >
                        <Text size="xs" c={share == null ? 'dimmed' : undefined}>
                            {formatShare(share)}
                        </Text>
                    </Tooltip>
                );
            },
        };
        // Names every level the grid is actually showing, so the header reads as the path
        // you drill down: "By category / By product group / SKU".
        const identityHeader =
            groupBy === 'NONE'
                ? isMaterial
                    ? 'Material'
                    : 'SKU'
                : [labelOf(groupBy), subGroupBy !== 'NONE' && labelOf(subGroupBy), leafLabel]
                      .filter(Boolean)
                      .join(' / ');

        const base: DataSheetColumn<HealthRow>[] = [
            {
                key: 'productId',
                header: identityHeader,
                sticky: true,
                width: 280,
                /*
                 * A group row's `productId` is a synthetic key (`group:CATEGORY:Plates`), which
                 * is meaningless in a spreadsheet. Export the label the grid shows instead, so
                 * the CSV's hierarchy columns read as the group names the operator recognises.
                 */
                csvValue: (_value, row) =>
                    row.isGroup ? (row.productName ?? '') : row.productId,
                render: (_value, row) => (
                    <Stack gap={0}>
                        <Text size="xs" fw={row.isGroup ? 600 : 500}>
                            {row.isGroup
                                ? `${row.productName} · ${row.childCount} ${leafLabel}(s)`
                                : row.productId}
                        </Text>
                        {!row.isGroup && row.productName && (
                            <Text size="10px" c="dimmed" truncate>
                                {row.productName}
                            </Text>
                        )}
                        {/* MOQ rides here as subtext on the supplier group rather than
                            occupying a column — it is only meaningful next to the order
                            you are about to place. */}
                        {row.isGroup && row.moq != null && (
                            <Text size="10px" c="dimmed">
                                MOQ {row.moq}
                            </Text>
                        )}
                    </Stack>
                ),
            },
            ...(isMaterial
                ? // Materials sit in two places that matter differently: the brand's own
                  // warehouses (needs a transfer before it can be consumed) and the factory
                  // floor (ready to use). Disjoint, and they sum to on-hand.
                  ([
                      {
                          key: 'stockInternal',
                          header: 'Internal stock',
                          align: 'right',
                          width: 130,
                          sortable: true,
                          render: (_value, row) => (
                              <Group gap={4} justify="flex-end" wrap="nowrap">
                                  <Text size="xs">{row.stockInternal}</Text>
                                  {row.unit && (
                                      <Text size="10px" c="dimmed">
                                          {row.unit}
                                      </Text>
                                  )}
                              </Group>
                          ),
                      },
                      {
                          key: 'stockAtManufacturer',
                          header: 'At manufacturer',
                          align: 'right',
                          width: 140,
                          sortable: true,
                          render: (_value, row) => (
                              <Text size="xs" c={row.stockAtManufacturer > 0 ? undefined : 'dimmed'}>
                                  {row.stockAtManufacturer > 0 ? row.stockAtManufacturer : '—'}
                              </Text>
                          ),
                      },
                      {
                          key: 'inTransitInternal',
                          header: 'In transit · internal',
                          align: 'right',
                          width: 150,
                          sortable: true,
                          render: (_value, row) => (
                              <Text size="xs" c={row.inTransitInternal > 0 ? undefined : 'dimmed'}>
                                  {row.inTransitInternal > 0 ? row.inTransitInternal : '—'}
                              </Text>
                          ),
                      },
                      {
                          key: 'inTransitAtManufacturer',
                          header: 'In transit · to manufacturer',
                          align: 'right',
                          width: 190,
                          sortable: true,
                          render: (_value, row) => (
                              <Text
                                  size="xs"
                                  c={row.inTransitAtManufacturer > 0 ? undefined : 'dimmed'}
                              >
                                  {row.inTransitAtManufacturer > 0
                                      ? row.inTransitAtManufacturer
                                      : '—'}
                              </Text>
                          ),
                      },
                  ] as DataSheetColumn<HealthRow>[])
                : ([
                      {
                          key: 'currentStock',
                          header: 'On hand',
                          align: 'right',
                          width: 110,
                          sortable: true,
                          // currentStock includes Stock Transfer (cover and shortfall count
                          // it); this column is the shelf part, the next one the moving part.
                          valueAccessor: (row) => row.currentStock - (row.stockTransfer ?? 0),
                          csvValue: (_value, row) => row.currentStock - (row.stockTransfer ?? 0),
                          render: (_value, row) => (
                              <Text size="xs">{row.currentStock - (row.stockTransfer ?? 0)}</Text>
                          ),
                      },
                      ...(hasStockTransfer
                          ? ([
                                {
                                    key: 'stockTransfer',
                                    header: STOCK_TRANSFER_LABEL,
                                    renderHeader: () => <StockTransferHeader />,
                                    align: 'right',
                                    width: 130,
                                    sortable: true,
                                    valueAccessor: (row) => row.stockTransfer ?? null,
                                    csvValue: (_value, row) => row.stockTransfer ?? '',
                                    render: (_value, row) => {
                                        const value = row.stockTransfer ?? 0;
                                        if (!value) {
                                            return (
                                                <Text size="xs" c="dimmed">
                                                    0
                                                </Text>
                                            );
                                        }
                                        // Group rows span many SKUs; the drawer is per SKU.
                                        if (row.isGroup || !onStockTransferClick) {
                                            return <Text size="xs">{value}</Text>;
                                        }
                                        return (
                                            <Anchor
                                                component="button"
                                                size="xs"
                                                title="View the stock transfers"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    onStockTransferClick(row);
                                                }}
                                            >
                                                {value}
                                            </Anchor>
                                        );
                                    },
                                },
                            ] as DataSheetColumn<HealthRow>[])
                          : []),
                  ] as DataSheetColumn<HealthRow>[])),
            {
                /*
                 * The name the grid shows as subtext under the SKU. It has no column of its own
                 * on screen — a second text column would crowd the numbers — but a spreadsheet
                 * has no subtext, so without this the download carried bare product ids.
                 */
                key: 'productName',
                header: isMaterial ? 'Material name' : 'Product name',
                csvOnly: true,
                csvValue: (_value, row) => row.productName ?? '',
            },
            // Inbound split in two, because the two halves are acted on differently. IN
            // TRANSIT is already moving — you can only wait for it, and the question is
            // when. OPEN is still with the maker — it is the part you can still chase,
            // expedite or cancel. One "Inbound" number hid that distinction behind a total.
            {
                key: 'inTransit',
                header: 'In transit',
                align: 'right',
                width: 120,
                sortable: true,
                // The figure lives under `inbound`, so sort and the table agent are pointed
                // at it explicitly — the default row[key] would compare undefined.
                valueAccessor: (row) => row.inbound.inTransit,
                // The CSV builder reads row[key], which is undefined here — without this
                // the column would export as a blank rather than the figure on screen.
                csvValue: (_value, row) => row.inbound.inTransit,
                render: (_value, row) =>
                    row.isGroup ? (
                        <Text size="xs">{row.inbound.inTransit}</Text>
                    ) : (
                        <Tooltip
                            label={`PO ${row.inbound.inTransitPo} · STN ${row.inbound.inTransitStn} — shipped and moving`}
                        >
                            <Anchor
                                component="button"
                                type="button"
                                size="xs"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setInboundFor({ row, focus: 'IN_TRANSIT' });
                                }}
                            >
                                {row.inbound.inTransit}
                            </Anchor>
                        </Tooltip>
                    ),
            },
            {
                key: 'open',
                header: 'Open',
                align: 'right',
                width: 120,
                sortable: true,
                valueAccessor: (row) => row.inbound.open,
                csvValue: (_value, row) => row.inbound.open,
                render: (_value, row) =>
                    row.isGroup ? (
                        <Text size="xs">{row.inbound.open}</Text>
                    ) : (
                        <Tooltip
                            label={[
                                `In production: PO ${row.inbound.inProductionPo}`,
                                !isMaterial &&
                                    `production orders ${row.inbound.inProductionWo}`,
                                `· Pending: PO ${row.inbound.pendingPo ?? 0}`,
                                !isMaterial && `production orders ${row.inbound.pendingWo ?? 0}`,
                            ]
                                .filter(Boolean)
                                .join(' · ')}
                        >
                            <Anchor
                                component="button"
                                type="button"
                                size="xs"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setInboundFor({ row, focus: 'OPEN' });
                                }}
                            >
                                {row.inbound.open}
                            </Anchor>
                        </Tooltip>
                    ),
            },
        ];

        // Material-only: how much finished-goods deficit leans on this material today, and
        // the quantity that implies at the BOM ratio. Snapshot arithmetic, no plan involved.
        if (isMaterial) {
            base.push(
                {
                    key: 'fgDeficit',
                    header: 'FG deficit',
                    align: 'right',
                    width: 110,
                    sortable: true,
                    render: (_value, row) => (
                        <Text size="xs" c={row.fgDeficit > 0 ? undefined : 'dimmed'}>
                            {row.fgDeficit > 0 ? row.fgDeficit : '—'}
                        </Text>
                    ),
                },
                {
                    key: 'rmRequired',
                    header: 'RM needed',
                    align: 'right',
                    width: 120,
                    sortable: true,
                    render: (_value, row) => (
                        <Group gap={4} justify="flex-end" wrap="nowrap">
                            <Text size="xs" fw={row.shortfall > 0 ? 600 : 400}>
                                {row.rmRequired > 0 ? row.rmRequired : '—'}
                            </Text>
                            {row.unit && row.rmRequired > 0 && (
                                <Text size="10px" c="dimmed">
                                    {row.unit}
                                </Text>
                            )}
                        </Group>
                    ),
                },
                // Straight after the figure it is a share of.
                contributionColumn,
                {
                    key: 'shortfall',
                    header: 'To order',
                    align: 'right',
                    width: 110,
                    sortable: true,
                    render: (_value, row) => (
                        <Text
                            size="xs"
                            fw={row.shortfall > 0 ? 600 : 400}
                            c={row.shortfall > 0 ? 'red' : undefined}
                        >
                            {row.shortfall > 0 ? row.shortfall : '—'}
                        </Text>
                    ),
                },
            );
        }

        // Reorder point, shortfall and cover are finished-goods concepts. A material's
        // requirement comes from exploding the FG plan through the BOM, not from its own
        // demand — so those columns are omitted rather than shown as zeroes.
        if (!isMaterial) {
            base.splice(1, 0, {
                key: 'reorderPoint',
                header: 'Reorder point',
                align: 'right',
                width: 130,
                sortable: true,
                render: (_value, row) =>
                    row.isGroup ? (
                        <Tooltip label="Sum of each SKU's reorder point. Shortfall is summed per SKU, so a group holding surplus on one SKU and short on another shows more shortfall than this total minus stock.">
                            <Text size="xs" ta="right">
                                {row.reorderPoint}
                            </Text>
                        </Tooltip>
                    ) : (
                        <Group gap={4} justify="flex-end" wrap="nowrap">
                            {/*
                              * The rule, spelled out on the number itself, in whichever of the
                              * two forms actually produced it. A threshold nobody can explain
                              * is a threshold nobody trusts, and the two bases arrive at one
                              * by genuinely different routes:
                              *
                              *   past sales — a rate read over a chosen lookback, then
                              *     multiplied up. The lookback is named because it is the term
                              *     that most often explains why two SKUs on the same tier with
                              *     the same lead show different thresholds.
                              *   forecast — the demand expected before the order lands. No
                              *     lookback took part, so naming one would invent a term.
                              */}
                            <Tooltip
                                label={
                                    row.ropWindowSource === 'COVERAGE'
                                        ? `${row.reorderTier ? `Tier ${row.reorderTier}: ` : ''}forecast over ${row.ropWindowDays}d (${row.reorderLeadTimeDays}d lead${row.reorderSafetyDays > 0 ? ` + ${row.reorderSafetyDays}d safety` : ''}) — ${row.ropDrr}/day expected`
                                        : `${row.reorderTier ? `Tier ${row.reorderTier}: ` : ''}${row.ropWindowDays}d DRR ${row.ropDrr} x (${row.reorderLeadTimeDays}d lead${row.reorderSafetyDays > 0 ? ` + ${row.reorderSafetyDays}d safety` : ''})`
                                }
                            >
                                <Text size="xs">{row.reorderPoint}</Text>
                            </Tooltip>
                            {/*
                              * The window lives ON the reorder point rather than in a column of
                              * its own: it is a property of the threshold, not a peer of it.
                              *
                              * READ-ONLY. The window is set as a POLICY — one number for the
                              * brand in Reorder settings, refined per tier there, and per SKU by
                              * bulk upload — not row by row on a grid. In-grid editing put a
                              * commitment behind a control that looks like a view filter, and
                              * offered an exception on every row for a decision most brands make
                              * once. The badge still explains which rung of the chain set this
                              * SKU's window and flags one that differs from the view.
                              */}
                            <RopWindowCell
                                row={row}
                                draft={undefined}
                                onChange={NOOP_WINDOW_CHANGE}
                                ready={false}
                                viewWindow={effectiveWindow}
                                editable={false}
                            />
                        </Group>
                    ),
            });
            base.push(
                {
                    key: 'shortfall',
                    header: 'Shortfall vs ROP',
                    align: 'right',
                    width: 140,
                    sortable: true,
                    render: (_value, row) => (
                        <Text
                            size="xs"
                            fw={row.shortfall > 0 ? 600 : 400}
                            c={row.shortfall > 0 ? 'red' : undefined}
                        >
                            {row.shortfall > 0 ? row.shortfall : '—'}
                        </Text>
                    ),
                },
                {
                    key: 'drr',
                    header: 'DRR',
                    align: 'right',
                    width: 128,
                    sortable: true,
                    render: (_value, row) => (
                        <Group gap={4} justify="flex-end" wrap="nowrap">
                            <Text size="xs">{row.drr}</Text>
                            <DrrDelta
                                row={row}
                                windowDays={effectiveWindow}
                                basis={effectiveBasis}
                            />
                        </Group>
                    ),
                },
                {
                    /*
                     * The move against the previous window of the same length, as its own
                     * CSV column. It rides beside the DRR figure on screen — the two are one
                     * reading — but a spreadsheet has no room for that, and a trend somebody
                     * can only see in the browser is a trend they cannot chart.
                     */
                    key: 'drrDeltaPct',
                    header: 'DRR change %',
                    csvOnly: true,
                    csvValue: (_value, row) =>
                        row.drrDeltaPct == null ? '' : row.drrDeltaPct,
                },
                // Straight after the figure it is a share of.
                contributionColumn,
                {
                    key: 'coverDays',
                    header: 'Cover (days)',
                    align: 'right',
                    width: 110,
                    sortable: true,
                    render: (_value, row) =>
                        row.coverDays == null ? (
                            <Text size="xs" c="dimmed">
                                —
                            </Text>
                        ) : (
                            <Text size="xs">{row.coverDays}</Text>
                        ),
                },
            );
        }

        base.push(
            {
                key: 'vendorName',
                header: 'Supplier',
                width: 180,
                render: (_value, row) => (
                    <Text size="xs" c={row.vendorName ? undefined : 'dimmed'}>
                        {row.vendorName || (row.isGroup ? '—' : 'Unassigned')}
                    </Text>
                ),
            },
            {
                key: 'sourcingModel',
                header: '',
                width: 56,
                align: 'center',
                // Deliberately quiet: an icon the operator reaches for when they want it,
                // not a call-to-action competing with the numbers on every row.
                render: (_value, row) => {
                    const placement = placementFor(row);
                    if (!placement) return null;
                    return (
                        <Tooltip label={placement.label}>
                            <ActionIcon
                                size="sm"
                                variant="subtle"
                                aria-label={placement.label}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    placement.onClick();
                                }}
                            >
                                {row.sourcingModel === 'MAKE' && !isMaterial ? (
                                    <FactoryIcon size={14} />
                                ) : (
                                    <ShoppingCartIcon size={14} />
                                )}
                            </ActionIcon>
                        </Tooltip>
                    );
                },
            },
        );

        return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        groupBy,
        subGroupBy,
        isMaterial,
        placementFor,
        contributions,
        // The window column renders a live control and the reorder-point badge compares
        // against the viewed window, so both have to rebuild when either moves.
        effectiveWindow,
        hasStockTransfer,
        onStockTransferClick,
    ]);

    /**
     * The totals row DataSheet pins directly under the column headers, so the figure for
     * each column sits in that column rather than in a separate summary strip the reader has
     * to map back onto the grid by eye.
     *
     * Only the additive columns carry a number. Cover-days is recomputed from the totals
     * (stock ÷ run rate) rather than summed, contribution is 100% by definition, and the
     * label, supplier and action columns are left blank — a total of a supplier name is not
     * a thing, and a placeholder there would read as data.
     */
    const aggregateRow = useMemo<Record<string, ReactNode>>(() => {
        const num = (value: number, digits = 0): string =>
            formatCount(value, { decimals: digits });
        return {
            productId: `Total · ${formatCount(totals.rowCount)} ${
                isMaterial ? 'material' : 'SKU'
            }(s)`,
            // The shelf part, matching the column; Stock Transfer has its own total.
            currentStock: num(totals.sums.currentStock - (totals.sums.stockTransfer ?? 0)),
            stockTransfer: num(totals.sums.stockTransfer ?? 0),
            stockInternal: num(totals.sums.stockInternal),
            stockAtManufacturer: num(totals.sums.stockAtManufacturer),
            inTransitInternal: num(totals.sums.inTransitInternal),
            inTransitAtManufacturer: num(totals.sums.inTransitAtManufacturer),
            inTransit: num(totals.sums.inTransit),
            open: num(totals.sums.open),
            reorderPoint: num(totals.sums.reorderPoint),
            shortfall: num(totals.sums.shortfall),
            drr: num(totals.sums.drr, 2),
            // A group of SKUs has no single window, so the cell reports the page's demand move
            // instead — the one thing that IS meaningful across a mixed set of policies.
            ropWindowDays:
                totals.drrDeltaPct == null
                    ? '—'
                    : `${totals.drrDeltaPct > 0 ? '+' : ''}${totals.drrDeltaPct}% DRR`,
            fgDeficit: num(totals.sums.fgDeficit),
            rmRequired: num(totals.sums.rmRequired, 2),
            coverDays: totals.coverDays == null ? '—' : num(totals.coverDays, 1),
            contribution: '100%',
        };
    }, [totals, isMaterial]);

    /**
     * Which columns the picker offers, derived from the ones actually built rather than a
     * parallel list — Finished goods and Materials render different sets, and a hand-kept
     * list would drift the moment either changes. The identity column is the row's name
     * and the sourcing column is its action affordance, so neither can be hidden.
     */
    const columnDefs = useMemo(
        () =>
            allColumns
                .filter((col) => !NON_HIDEABLE_COLUMNS.has(col.key) && col.header)
                .map((col) => ({ key: col.key, label: col.header })),
        [allColumns],
    );

    // A key hidden in one item view is remembered for the other; the sets barely overlap,
    // so a Materials-only choice cannot strip a Finished-goods column it never applied to.
    /**
     * SKUs picked for the Plan handoff, by productId. Lives outside `allColumns` so a
     * toggle re-renders one cheap column instead of rebuilding the whole column set.
     */
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    const selectColumn = useMemo<DataSheetColumn<HealthRow> | null>(() => {
        if (isMaterial || !onPlanForSkus) return null;
        const leavesOf = (row: HealthRow): HealthRow[] =>
            row.isGroup ? (row.subRows ?? []).flatMap(leavesOf) : [row];
        const toggle = (ids: string[], allPicked: boolean): void => {
            setSelectedIds((prev) => {
                const next = new Set(prev);
                for (const id of ids) {
                    if (allPicked) next.delete(id);
                    else next.add(id);
                }
                return next;
            });
        };
        return {
            key: 'select',
            header: '',
            width: 40,
            sticky: true,
            sortable: false,
            filterable: false,
            csvValue: () => '',
            render: (_value, row) => {
                // A group row's checkbox stands for all its leaves — indeterminate when
                // only some are picked.
                const ids = leavesOf(row).map((leaf) => leaf.productId);
                const picked = ids.filter((id) => selectedIds.has(id)).length;
                return (
                    <Checkbox
                        size="xs"
                        checked={picked > 0 && picked === ids.length}
                        indeterminate={picked > 0 && picked < ids.length}
                        onChange={() => toggle(ids, picked === ids.length)}
                        onClick={(event) => event.stopPropagation()}
                        aria-label="Select for planning"
                    />
                );
            },
        };
    }, [isMaterial, onPlanForSkus, selectedIds]);

    const columns = useMemo(
        () => [
            ...(selectColumn ? [selectColumn] : []),
            ...allColumns.filter((col) => !hiddenCols.includes(col.key)),
        ],
        [selectColumn, allColumns, hiddenCols],
    );


    /**
     * Downloads are always one row per item, even when the grid is grouped — a CSV of
     * group subtotals loses the detail the file is wanted for, and it is what the
     * Snapshot already does (it re-fetches ungrouped rows for its export). Everything
     * is already in memory here, so this resolves immediately rather than re-fetching.
     */


    const toolbarControls = (
        <Group gap="sm" wrap="nowrap">
            <SegmentedControl
                size="xs"
                value={itemView}
                onChange={(value) => {
                    setItemView(value as ItemView);
                    // A material is not a plannable SKU; carrying the pick across views
                    // would hand Plan mode products the operator never sees selected.
                    setSelectedIds(new Set());
                }}
                data={[
                    { value: 'product', label: 'Finished goods' },
                    { value: 'material', label: 'Materials' },
                ]}
            />
            <GroupingLevels
                options={groupingOptions}
                noneValue="NONE"
                groupBy={groupBy}
                subGroupBy={subGroupBy}
                onChange={(next, nextSub) => {
                    setGroupingChoice({
                        groupBy: next as GroupBy,
                        subGroupBy: nextSub as GroupBy,
                    });
                }}
                hint="Rows roll up on the first level, then on the second inside it. Quantities sum at both levels; cover is recomputed from the group's own stock and run rate."
            />
            {!isMaterial && (
                <Select
                    size="xs"
                    w={165}
                    value={sourcing}
                    onChange={(value) => setSourcing(value ?? 'ALL')}
                    data={SOURCING_FILTERS}
                    allowDeselect={false}
                />
            )}
            <Select
                size="xs"
                w={150}
                value={sortBy}
                onChange={(value) => setSortBy((value as SortBy) ?? 'DEFAULT')}
                data={SORT_OPTIONS}
                allowDeselect={false}
            />
            {!isMaterial && (
                <RateBasisControl
                    metricLabel="DRR"
                    savedBasis={savedBasis}
                    effectiveBasis={effectiveBasis}
                    effectiveWindow={effectiveWindow}
                    isDirty={rateDirty}
                    saving={savingRate}
                    onBasisChange={setDraftBasis}
                    onWindowChange={setDraftWindow}
                    onSave={handleSaveRateDefault}
                    onReset={() => {
                        setDraftBasis(null);
                        setDraftWindow(null);
                    }}
                />
            )}
            {/*
              * ONE entry point for everything the reorder point is made of — how it reads
              * demand, over what window, and the per-tier and per-SKU refinements of that.
              * These used to be three separate links sitting between view controls, which
              * read as three unrelated toggles rather than as one policy.
              *
              * Deliberately NOT beside the DRR control above, and it earns the separation:
              * that one is exploration (re-base the page and look), this one is a commitment
              * the brand buys against and that holds still while somebody explores.
              */}
            {!isMaterial && (
                <Button
                    size="xs"
                    variant={reorderRulesOpen ? 'light' : 'subtle'}
                    color="gray"
                    onClick={() => setReorderRulesOpen(true)}
                >
                    Reorder settings
                </Button>
            )}
        </Group>
    );

    // Counted off the rows actually in scope rather than the server's response, so the
    // sourcing filter — now applied here rather than server-side — moves the headline with
    // the grid instead of leaving it describing a set the operator has filtered away.
    const belowRopInScope = useMemo(
        () => filteredRows.filter((row) => row.shortfall > 0).length,
        [filteredRows],
    );

    const subtitle = loading
        ? 'Loading…'
        : isMaterial
          ? `${filteredRows.length} material(s) · stock and incoming from open material orders`
          : `${belowRopInScope} of ${filteredRows.length} SKU(s) below their reorder point · fallback target ${targetDoh ?? '—'} days of cover`;

    /*
     * The summary reads the response, not the grid — so grouping, the break-down toggle and the
     * column picker do not change its numbers. Filters that narrow the SET do travel, because a
     * summary of rows the operator has filtered away would describe a table nobody is looking at.
     */
    const summarySources = useMemo(
        () => ({
            inventoryHealth: {
                data: filteredRows,
                targetDaysOfCover: targetDoh,
                belowReorderPoint: belowRopInScope,
                total: filteredRows.length,
            },
        }),
        [filteredRows, targetDoh, belowRopInScope],
    );

    return (
        <Stack gap={8} px="md" pb="md">
            {!isMaterial && !loading && filteredRows.length > 0 && (
                <AiSummaryPanel
                    title="Inventory health summary"
                    spec={INVENTORY_HEALTH_SUMMARY_SPEC}
                    sources={summarySources}
                    collapsed={summaryCollapsed}
                    onToggleCollapse={() => setSummaryCollapsed(!summaryCollapsed)}
                />
            )}

            {selectedIds.size > 0 && onPlanForSkus && (
                <Group gap="sm" justify="space-between">
                    <Text size="sm" fw={600}>
                        {selectedIds.size} SKU{selectedIds.size === 1 ? '' : 's'} selected
                    </Text>
                    <Group gap="sm">
                        <Anchor size="sm" onClick={() => setSelectedIds(new Set())}>
                            clear
                        </Anchor>
                        <Button
                            size="xs"
                            onClick={() => {
                                onPlanForSkus([...selectedIds]);
                                setSelectedIds(new Set());
                            }}
                        >
                            Plan for these SKUs →
                        </Button>
                    </Group>
                </Group>
            )}

            <DataSheet<HealthRow>
                data={visibleRows}
                columns={columns}
                captureRef={captureRef}
                title="Inventory health"
                subtitle={subtitle}
                isLoading={loading}
                getRowKey={(row) => row.rowKey}
                getSubRows={(row) => row.subRows}
                explosion={{
                    ...explosion,
                    // Makes the shared `+N` badge clickable: it opens the arithmetic rather
                    // than only naming the contributors in a tooltip.
                    onShowDetail: (row) => setExplosionFor(row),
                }}
                aggregateRow={aggregateRow}
                onFilteredRowsChange={setGridRows}
                enableSearch
                searchPlaceholder={isMaterial ? 'Search material or name...' : 'Search SKU or name...'}
                searchValue={search}
                onSearchChange={setSearch}
                toolbarControls={toolbarControls}
                toolbarActionsRight={
                    <Group gap={4} wrap="nowrap">
                        <ColumnPicker
                            defaultOnDefs={columnDefs}
                            defaultOffDefs={[]}
                            hiddenKeys={hiddenCols}
                            onChange={setHiddenCols}
                        />
                    </Group>
                }
                enableDownload
                /*
                 * No `onFetchAllData`: every row is already here, so DataSheet exports the
                 * VIEW — these columns, the rows after its own column filters, and the group
                 * tree expanded so each group total is followed by the SKUs behind it.
                 *
                 * It used to hand over `flatRows`, which silently dropped the grouping and any
                 * column filter the user had applied. A grouped screen then downloaded as an
                 * ungrouped file, and the % contribution column — a share of its parent — had
                 * nothing left to be a share of.
                 */
                downloadFilename={
                    isMaterial
                        ? 'inventory_health_materials.csv'
                        : 'inventory_health.csv'
                }
                maxHeight="calc(100vh - 260px)"
                emptyState={isMaterial ? 'No materials for this scope.' : 'No stock data for this scope.'}
            />

            <InboundDetailPanel
                productId={inboundFor?.row.productId ?? null}
                focus={inboundFor?.focus ?? null}
                // The parents this row absorbed, so the window adds up to the cell that
                // opened it. Empty unless break-down is on AND this row actually took
                // inbound from a parent, which is rare.
                extraProductIds={
                    inboundFor
                        ? explosionSourceIds(
                              inboundFor.row,
                              inboundFor.row.productId,
                          ).filter((id) => id !== inboundFor.row.productId)
                        : undefined
                }
                itemType={itemView}
                onClose={() => setInboundFor(null)}
            />


            <ExplosionDetailPanel
                row={explosionFor}
                sourceRows={sourceRowsById}
                onClose={() => setExplosionFor(null)}
            />

            {stockTransferDrill && (
                <StockTransferModal
                    isOpen
                    onClose={() => setStockTransferDrill(null)}
                    productId={stockTransferDrill.productId}
                    skuLabel={stockTransferDrill.skuLabel}
                />
            )}
            <ReplenishmentSettingsModal
                opened={reorderRulesOpen}
                mode="REORDER"
                // The per-SKU windows live here too, as a bulk upload rather than a grid
                // control: it is the same policy at a finer grain, so it belongs behind the
                // same door as the merchant default and the tier rules.
                onOpenUpload={() => {
                    setReorderRulesOpen(false);
                    setUploadOpen(true);
                }}
                onClose={() => {
                    setReorderRulesOpen(false);
                    // Reorder points are resolved server-side on every read, so the grid has
                    // to refetch to show the new thresholds.
                    load();
                }}
            />

            <ReorderPointUploadModal
                opened={uploadOpen}
                onClose={() => setUploadOpen(false)}
                onUploaded={load}
            />

            <CreatePoFromShortfallModal
                opened={poDraft !== null}
                // A group row hands over its children; a leaf row hands over itself, so the
                // review panel always receives the same shape.
                rows={poDraft ? (poDraft.subRows ?? [poDraft]) : []}
                vendorId={poDraft?.vendorId ?? null}
                vendorName={poDraft?.vendorName ?? null}
                onClose={() => setPoDraft(null)}
                onCreated={load}
            />

            <NewWorkOrderModal
                opened={woDraft !== null}
                onClose={() => setWoDraft(null)}
                onCreated={() => {
                    setWoDraft(null);
                    load();
                }}
                prefill={
                    woDraft
                        ? {
                              // A manufacturer group hands over every child as a line; a leaf
                              // row is a one-line order. Quantities seed from what each row is
                              // actually short by and stay editable in the modal.
                              lines: (woDraft.subRows ?? [woDraft]).map((row) => ({
                                  productId: row.productId,
                                  quantity: Math.max(0, row.shortfall),
                                  label: row.productName
                                      ? `${row.productId} — ${row.productName}`
                                      : row.productId,
                              })),
                              dueDate: '',
                              // MAKE rows are sourced from a manufacturer, so the order is
                              // addressed to that vendor rather than one of our warehouses.
                              ...(woDraft.vendorId
                                  ? {
                                        locationType: 'VENDOR' as const,
                                        locationId: woDraft.vendorId,
                                    }
                                  : {}),
                          }
                        : null
                }
            />
        </Stack>
    );
};

export default InventoryHealthView;
